import React, { useEffect, useState } from "react";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput } from "mui-tel-input";
import { useSite } from "hooks/use-site";
import { useAppDispatch, useAppSelector } from "store";
import { useLocation, useNavigate } from "react-router-dom";
import { errorNotification } from "../../features/notifications/notificationsSlice";
import { getDefaultKycPrograms, signUpIledgends } from "../../service/api";
import { NewUser, setNewUser } from "../../features/register/registerSlice";

type InitialValues = {
  email: string;
  phone: string;
  password: string;
  passwordConfirmation: string;
  terms_agree_check: boolean;
  privacy_agree_check: boolean;
  age_agree_check: boolean;
};

type WalletProgram = {
  id: number;
  wallet_type: "Private Wallet" | "Business Wallet";
  default_currency: string;
  disable_address_fields_in_registration_form: number;
};

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Email is invalid").required("Email is required"),
  phone: Yup.string()
    .required("Mobile is required")
    .test("is-valid-phone", "Invalid phone number", (value) => {
      if (!value) return false;
      const cleaned = value.replace(/\D/g, "");
      return cleaned.length >= 10 && cleaned.length <= 15;
    }),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Must be at least 8 characters")
    .matches(/[a-z]/, "Lowercase required")
    .matches(/[A-Z]/, "Uppercase required")
    .matches(/[0-9]/, "Number required"),
  passwordConfirmation: Yup.string().oneOf(
    [Yup.ref("password"), null],
    "Passwords must match"
  ),
  terms_agree_check: Yup.boolean().oneOf([true], "You must agree to terms"),
  privacy_agree_check: Yup.boolean().oneOf(
    [true],
    "You must agree to privacy policy"
  ),
  age_agree_check: Yup.boolean().oneOf([true], "You must confirm age"),
});

const SignUpWithPassword = () => {
  const navigate = useNavigate();
  const {
    logo: logoSrc,
    termsAndConditionsUrl,
    privacyAndPolicyUrl,
    frontWalletAuthPageColor,
  } = useSite();
  const { search } = useLocation();
  const typeParam = new URLSearchParams(search).get("type");
  const desiredType =
    typeParam === "business" ? "Business Wallet" : "Private Wallet";
  const [kycProgramId, setKycProgramId] = useState<number | null>(null);
  const dispatch = useAppDispatch();
  const newUser = useAppSelector((state) => state.register.newUser);

  useEffect(() => {
    getDefaultKycPrograms().then(
      (data: { walletPrograms: WalletProgram[] }) => {
        const prog = data.walletPrograms.find(
          (p: WalletProgram) => p.wallet_type === desiredType
        );
        if (prog) {
          setKycProgramId(prog.id);
        }
      }
    );
  }, [desiredType]);

  const {
    handleSubmit,
    getFieldMeta,
    setFieldValue,
    handleChange,
    handleBlur,
    isValid,
    isSubmitting,
  } = useFormik<InitialValues>({
    initialValues: {
      email: "",
      phone: "",
      password: "",
      passwordConfirmation: "",
      terms_agree_check: false,
      privacy_agree_check: false,
      age_agree_check: false,
    },
    validationSchema,
    onSubmit: async (
      { email, phone, password },
      { setSubmitting }: FormikHelpers<InitialValues>
    ) => {
      setSubmitting(true);
      try {
        if (kycProgramId == null) {
          dispatch(errorNotification("KYC program is not loaded yet"));
          return;
        }

        await signUpIledgends({
          email,
          phone,
          password,
          kyc_program_id: kycProgramId,
        });

        dispatch(setNewUser({ ...newUser, email, phone } as NewUser));
        navigate("/registration-completed");
      } catch (err: any) {
        dispatch(errorNotification(err.message || "Registration failed"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="md">
        <form onSubmit={handleSubmit}>
          <Card elevation={24} component={motion.div} layoutId="auth-card">
            <Box py={2.5} px={4} sx={{ bgcolor: frontWalletAuthPageColor }}>
              <img src={logoSrc} alt="logo" width={200} />
            </Box>
            <CardContent>
              <Stack spacing={4}>
                <Box display="flex" alignItems="center">
                  <LockIcon fontSize="large" />
                  <Typography variant="h6" fontWeight={600}>
                    Create Account
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      name="email"
                      fullWidth
                      value={getFieldMeta("email").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        !!getFieldMeta("email").error &&
                        getFieldMeta("email").touched
                      }
                      helperText={getFieldMeta("email").error}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Password"
                      name="password"
                      type="password"
                      fullWidth
                      value={getFieldMeta("password").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        !!getFieldMeta("password").error &&
                        getFieldMeta("password").touched
                      }
                      helperText={getFieldMeta("password").error}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Retype Password"
                      name="passwordConfirmation"
                      type="password"
                      fullWidth
                      value={getFieldMeta("passwordConfirmation").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        !!getFieldMeta("passwordConfirmation").error &&
                        getFieldMeta("passwordConfirmation").touched
                      }
                      helperText={getFieldMeta("passwordConfirmation").error}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MuiTelInput
                      defaultCountry="UA"
                      fullWidth
                      label="Mobile"
                      name="phone"
                      value={getFieldMeta("phone").value}
                      onChange={(val) => setFieldValue("phone", val)}
                      error={
                        !!getFieldMeta("phone").error &&
                        getFieldMeta("phone").touched
                      }
                      helperText={getFieldMeta("phone").error}
                    />
                  </Grid>
                </Grid>
                <Stack>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={
                        <Typography component="span">
                          I agree to the{" "}
                          <Link href={termsAndConditionsUrl} target="_blank">
                            terms and conditions
                          </Link>
                        </Typography>
                      }
                      name="terms_agree_check"
                      checked={getFieldMeta("terms_agree_check").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {getFieldMeta("terms_agree_check").touched &&
                      getFieldMeta("terms_agree_check").error && (
                        <Typography color="error">
                          {getFieldMeta("terms_agree_check").error}
                        </Typography>
                      )}
                  </FormGroup>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={
                        <Typography component="span">
                          I agree to the{" "}
                          <Link href={privacyAndPolicyUrl} target="_blank">
                            privacy policy
                          </Link>
                        </Typography>
                      }
                      name="privacy_agree_check"
                      checked={getFieldMeta("privacy_agree_check").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {getFieldMeta("privacy_agree_check").touched &&
                      getFieldMeta("privacy_agree_check").error && (
                        <Typography color="error">
                          {getFieldMeta("privacy_agree_check").error}
                        </Typography>
                      )}
                  </FormGroup>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      label="I am over 18 years old"
                      name="age_agree_check"
                      checked={getFieldMeta("age_agree_check").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {getFieldMeta("age_agree_check").touched &&
                      getFieldMeta("age_agree_check").error && (
                        <Typography color="error">
                          {getFieldMeta("age_agree_check").error}
                        </Typography>
                      )}
                  </FormGroup>
                  <Typography variant="subtitle1">
                    <p>
                      * For Corporate account; the details must be of a director
                      or an authorized signatory.
                    </p>
                  </Typography>

                  <Typography variant="subtitle1">
                    <p>
                      * The Personal details must be real and according to your
                      ID/Passport/Driving licence.{" "}
                    </p>
                  </Typography>
                  <Typography variant="subtitle1">
                    <p>
                      * To verify information obtained from customers all names
                      are screened with the KYC tool and searched through
                      credible sources of commercially or publicly available
                      information.{" "}
                    </p>
                  </Typography>
                </Stack>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => navigate("/signin")}
                  sx={{ textTransform: "none", mt: 1 }}
                >
                  Already have an account? Sign In
                </Button>
                <LoadingButton
                  fullWidth
                  loading={isSubmitting}
                  variant="contained"
                  type="submit"
                  disabled={!isValid || isSubmitting}
                >
                  Next
                </LoadingButton>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default SignUpWithPassword;
